import { Genre } from "@filmweb/sanity-types";
import classNames from "classnames";
import FilmwebLink from "src/components/filmweblink/FilmwebLink";
import { useLocationContext } from "src/lib/contexts/LocationContext";
import { type Show, type VersionTag } from "src/lib/movieinfo/movieinfotypes";
import styles from "./Show.module.css";

//#region [Props]
type ShowProps = {
    show: Show;
    isKinoklubbMovie?: boolean;
    genres?: Genre[];
    className?: string;
    style?: React.CSSProperties;
};
//#endregion



//#region [Component]
export default function Show({ show, isKinoklubbMovie = false, genres, className, style }: ShowProps) {
    const locationContext = useLocationContext();
    let disableShowLink = !show.ticketSaleUrl;

    return <FilmwebLink to={getTicketUrl(show.ticketSaleUrl, show.theaterName!, show.movieTitle!)}
        target="_blank"
        onClick={() => !disableShowLink ? trackSale(show, genres) : false}
        className={classNames(styles.SShow, isKinoklubbMovie && show.isKinoklubb && styles.isKinoklubb, className)}
        style={style}
    >
        <div className={classNames(styles.STime, isKinoklubbMovie && show.isKinoklubb && styles.SKKTime)}>{show.showStart.substring(11, 16)}</div>
        <div className={styles.SScreenLine}>
            <div className={classNames(styles.SScreenName, hasMultipleLocations(locationContext.location) && styles.isMultiLocation)} >
                {getScreenName(show, locationContext.location)}
            </div>
            <div className={styles.SVersionInfo}>
                {getVersionName(show)}
            </div>
        </div>
    </FilmwebLink>

}
//#endregion

//#region [Other]
const EXCLUDE_TAGS = ["Norsk tekst", "2D"];

function hasMultipleLocations(location: string | null | undefined): boolean {
    if (location === "Oslo og omegn") {
        return true;
    }

    return false;
}
// empty urls will end up rendered as divs
function getTicketUrl(url: string | undefined | null, theatre: string, title: string): string {
    if (!url) return "";
    if (/\?/.test(url)) {
        return url + `&utm_source=Filmweb&utm_campaign=Filmweb&utm_medium=${theatre}&utm_content=${title}&sender=Filmweb`;
    } else {
        return url + `?utm_source=Filmweb&utm_campaign=Filmweb&utm_medium=${theatre}&utm_content=${title}&sender=Filmweb`;
    }
}

function isNFkino(theaterName: string) {
    let result = false;
    switch (theaterName) {
        case "Asker kino":
        case "Colosseum":
        case "Gimle":
        case "Klingenberg":
        case "Ringen":
        case "Saga":
        case "Symra":
        case "Vika":
            result = true;
            break;
    }
    return result;
}

function getScreenName(show: Show, location: string | null | undefined): string {
    if (hasMultipleLocations(location)) {
        if (isNFkino(show.theaterName!)) {
            return "NFKino " + show.screenName;
        }
        else {
            return show.theaterName + " - " + show.screenName;
        }
    }
    return show.screenName!;
}

function getVersionName(show: Show) {
    const versionTags = (show.versionTags?.filter((versionTag) => !EXCLUDE_TAGS.includes(versionTag!.tag!)) ?? []) as VersionTag[];
    versionTags.sort((a, b) => {
        if (a.tag!.endsWith(" tale") && !b.tag!.endsWith(" tale")) {
            return -1;
        }
        if (!a.tag!.endsWith(" tale") && b.tag!.endsWith(" tale")) {
            return 1;
        }

        if (a.tag!.includes("tekst") && !b.tag!.includes("tekst")) {
            return 1;
        }
        if (!a.tag!.includes("tekst") && b.tag!.includes("tekst")) {
            return -1;
        }
        return 0;
    });

    if (show.showType) versionTags.push({ tag: show.showType, type: "" });

    const versionText = versionTags.length === 0 ? "\u00A0" : versionTags.map(t => t.tag).join(", ");
    return versionText;
}

function trackSale(show: Show, genres: Genre[] | undefined) {
    window?.dataLayer?.push({
        'event': 'Billettsalg',
        'eventCategory': 'Billettsalg',
        'eventAction': `${show.firmName} ${show.theaterName}`,
        'eventLabel': show.movieTitle,
        'eventGenres': genres?.map(g => g.name)?.join(" / ") ?? "",
        'eventEdi': show.movieVersionId
    });
}
//#endregion
